import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { Progress } from '@/components/ui/progress'
import {
  Megaphone,
  Image,
  MessageSquare,
  Mail,
  Smartphone,
  Bell,
  Layout,
  Send,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Copy,
  Clock,
  MousePointer,
  TrendingUp,
  Play,
  Pause,
  Upload,
  Save,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ThaiDatePicker } from '@/components/ui/date-time-picker'

// Types
interface Banner {
  id: string
  name: string
  image: string
  link: string
  position: 'home' | 'sidebar' | 'popup' | 'footer'
  status: 'active' | 'inactive' | 'scheduled'
  startDate: string
  endDate: string
  clicks: number
  impressions: number
  ctr: number
}

interface Campaign {
  id: string
  name: string
  type: 'sms' | 'line' | 'email' | 'push' | 'broadcast'
  status: 'draft' | 'scheduled' | 'running' | 'completed' | 'paused'
  targetAudience: string
  totalRecipients: number
  sent: number
  opened: number
  clicked: number
  scheduledAt?: string
  completedAt?: string
}

interface Popup {
  id: string
  name: string
  content: string
  trigger: 'immediate' | 'delay' | 'exit' | 'scroll'
  status: 'active' | 'inactive'
  views: number
  clicks: number
  conversionRate: number
}

// Mock Data
const mockBanners: Banner[] = [
  { id: '1', name: 'โปรโมชั่นสมัครใหม่ 100%', image: '/banner1.jpg', link: '/promotions/welcome', position: 'home', status: 'active', startDate: '2025-11-01', endDate: '2025-12-31', clicks: 15420, impressions: 125000, ctr: 12.34 },
  { id: '2', name: 'สล็อตแจกหนัก', image: '/banner2.jpg', link: '/games/slot', position: 'home', status: 'active', startDate: '2025-11-15', endDate: '2025-11-30', clicks: 8750, impressions: 85000, ctr: 10.29 },
  { id: '3', name: 'คืนยอดเสีย 10%', image: '/banner3.jpg', link: '/promotions/lossback', position: 'sidebar', status: 'scheduled', startDate: '2025-12-01', endDate: '2025-12-31', clicks: 0, impressions: 0, ctr: 0 },
  { id: '4', name: 'ฝากครั้งแรกรับ 50%', image: '/banner4.jpg', link: '/promotions/first-deposit', position: 'popup', status: 'inactive', startDate: '2025-10-01', endDate: '2025-10-31', clicks: 12500, impressions: 95000, ctr: 13.16 },
]

const mockCampaigns: Campaign[] = [
  { id: '1', name: 'โปรโมชั่นต้อนรับเดือน ธ.ค.', type: 'sms', status: 'scheduled', targetAudience: 'สมาชิกใหม่', totalRecipients: 5000, sent: 0, opened: 0, clicked: 0, scheduledAt: '2025-12-01 09:00' },
  { id: '2', name: 'แจ้งเตือน Cashback', type: 'line', status: 'running', targetAudience: 'สมาชิก Active', totalRecipients: 12500, sent: 8500, opened: 6200, clicked: 2100 },
  { id: '3', name: 'Newsletter ประจำสัปดาห์', type: 'email', status: 'completed', targetAudience: 'ทุกสมาชิก', totalRecipients: 25000, sent: 25000, opened: 8500, clicked: 2850, completedAt: '2025-11-25 10:30' },
  { id: '4', name: 'แจ้งเตือนโปรใหม่', type: 'push', status: 'draft', targetAudience: 'สมาชิก VIP', totalRecipients: 1500, sent: 0, opened: 0, clicked: 0 },
  { id: '5', name: 'ประกาศปิดปรับปรุง', type: 'broadcast', status: 'completed', targetAudience: 'ทุกสมาชิก', totalRecipients: 25000, sent: 25000, opened: 22000, clicked: 0, completedAt: '2025-11-20 08:00' },
]

const mockPopups: Popup[] = [
  { id: '1', name: 'Welcome Bonus Popup', content: 'สมัครวันนี้รับโบนัส 100%', trigger: 'immediate', status: 'active', views: 45000, clicks: 12500, conversionRate: 27.78 },
  { id: '2', name: 'Exit Intent Offer', content: 'อย่าพลาด! รับเครดิตฟรี 50 บาท', trigger: 'exit', status: 'active', views: 18500, clicks: 3200, conversionRate: 17.30 },
  { id: '3', name: 'First Deposit Reminder', content: 'ฝากครั้งแรกรับโบนัส 50%', trigger: 'delay', status: 'inactive', views: 0, clicks: 0, conversionRate: 0 },
]

const campaignTypeConfig: Record<Campaign['type'], { label: string; icon: React.ReactNode; color: string }> = {
  sms: { label: 'SMS', icon: <Smartphone className="h-4 w-4" />, color: 'bg-blue-500/10 text-blue-500' },
  line: { label: 'LINE', icon: <MessageSquare className="h-4 w-4" />, color: 'bg-green-500/10 text-green-500' },
  email: { label: 'Email', icon: <Mail className="h-4 w-4" />, color: 'bg-purple-500/10 text-purple-500' },
  push: { label: 'Push', icon: <Bell className="h-4 w-4" />, color: 'bg-orange-500/10 text-orange-500' },
  broadcast: { label: 'Broadcast', icon: <Megaphone className="h-4 w-4" />, color: 'bg-red-500/10 text-red-500' },
}

export function MarketingToolsPage() {
  const [activeTab, setActiveTab] = useState('banners')
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateBannerDialog, setShowCreateBannerDialog] = useState(false)
  const [showCreateCampaignDialog, setShowCreateCampaignDialog] = useState(false)
  const [showCreatePopupDialog, setShowCreatePopupDialog] = useState(false)

  const stats = useMemo(() => ({
    totalBanners: mockBanners.length,
    activeBanners: mockBanners.filter(b => b.status === 'active').length,
    totalCampaigns: mockCampaigns.length,
    runningCampaigns: mockCampaigns.filter(c => c.status === 'running').length,
    totalPopups: mockPopups.length,
    activePopups: mockPopups.filter(p => p.status === 'active').length,
  }), [])

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string }> = {
      active: { label: 'ใช้งาน', className: 'bg-win/10 text-win' },
      inactive: { label: 'ปิด', className: 'bg-muted text-muted-foreground' },
      scheduled: { label: 'กำหนดเวลา', className: 'bg-blue-500/10 text-blue-500' },
      draft: { label: 'แบบร่าง', className: 'bg-muted text-muted-foreground' },
      running: { label: 'กำลังทำงาน', className: 'bg-amber-500/10 text-amber-500' },
      completed: { label: 'เสร็จสิ้น', className: 'bg-win/10 text-win' },
      paused: { label: 'หยุดชั่วคราว', className: 'bg-orange-500/10 text-orange-500' },
    }
    const { label, className } = config[status] || { label: status, className: '' }
    return <Badge variant="secondary" className={cn("border-0", className)}>{label}</Badge>
  }

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" />
            เครื่องมือการตลาด
          </h1>
          <p className="text-muted-foreground">จัดการแบนเนอร์ แคมเปญ และเครื่องมือทำการตลาด</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Image className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">แบนเนอร์</p>
                <p className="text-xl font-bold">{stats.activeBanners}/{stats.totalBanners}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Send className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">แคมเปญ</p>
                <p className="text-xl font-bold">{stats.runningCampaigns}/{stats.totalCampaigns}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Layout className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ป๊อปอัพ</p>
                <p className="text-xl font-bold">{stats.activePopups}/{stats.totalPopups}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-win/10">
                <MousePointer className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คลิกวันนี้</p>
                <p className="text-xl font-bold text-win">2,458</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <Eye className="h-5 w-5 text-cyan-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">การเข้าชม</p>
                <p className="text-xl font-bold">125K</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-500/10">
                <TrendingUp className="h-5 w-5 text-pink-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Conversion</p>
                <p className="text-xl font-bold">8.5%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="banners" className="gap-2">
              <Image className="h-4 w-4" />
              แบนเนอร์
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="gap-2">
              <Send className="h-4 w-4" />
              แคมเปญ
            </TabsTrigger>
            <TabsTrigger value="popups" className="gap-2">
              <Layout className="h-4 w-4" />
              ป๊อปอัพ
            </TabsTrigger>
            <TabsTrigger value="broadcast" className="gap-2">
              <Megaphone className="h-4 w-4" />
              Broadcast
            </TabsTrigger>
          </TabsList>
          
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            {activeTab === 'banners' && (
              <Button className="gap-2" onClick={() => setShowCreateBannerDialog(true)}>
                <Plus className="h-4 w-4" />
                สร้างแบนเนอร์
              </Button>
            )}
            {activeTab === 'campaigns' && (
              <Button className="gap-2" onClick={() => setShowCreateCampaignDialog(true)}>
                <Plus className="h-4 w-4" />
                สร้างแคมเปญ
              </Button>
            )}
            {activeTab === 'popups' && (
              <Button className="gap-2" onClick={() => setShowCreatePopupDialog(true)}>
                <Plus className="h-4 w-4" />
                สร้างป๊อปอัพ
              </Button>
            )}
          </div>
        </div>

        {/* Banners Tab */}
        <TabsContent value="banners" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {mockBanners.map((banner) => (
              <Card key={banner.id} className="overflow-hidden">
                <div className="aspect-video bg-muted relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Image className="h-12 w-12 text-muted-foreground/50" />
                  </div>
                  <div className="absolute top-2 right-2">
                    {getStatusBadge(banner.status)}
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold line-clamp-1">{banner.name}</h3>
                      <p className="text-xs text-muted-foreground">ตำแหน่ง: {banner.position}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem className="gap-2">
                          <Eye className="h-4 w-4" /> ดูตัวอย่าง
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2">
                          <Edit className="h-4 w-4" /> แก้ไข
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2">
                          <Copy className="h-4 w-4" /> ทำสำเนา
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="gap-2 text-destructive">
                          <Trash2 className="h-4 w-4" /> ลบ
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-sm">
                    <div className="p-2 rounded-lg bg-muted/30">
                      <p className="font-semibold">{formatNumber(banner.impressions)}</p>
                      <p className="text-xs text-muted-foreground">แสดงผล</p>
                    </div>
                    <div className="p-2 rounded-lg bg-muted/30">
                      <p className="font-semibold">{formatNumber(banner.clicks)}</p>
                      <p className="text-xs text-muted-foreground">คลิก</p>
                    </div>
                    <div className="p-2 rounded-lg bg-muted/30">
                      <p className="font-semibold text-win">{banner.ctr}%</p>
                      <p className="text-xs text-muted-foreground">CTR</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    {banner.startDate} - {banner.endDate}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>แคมเปญ</TableHead>
                  <TableHead>ประเภท</TableHead>
                  <TableHead>กลุ่มเป้าหมาย</TableHead>
                  <TableHead className="text-center">ผู้รับ</TableHead>
                  <TableHead className="text-center">ส่งแล้ว</TableHead>
                  <TableHead className="text-center">เปิดอ่าน</TableHead>
                  <TableHead className="text-center">คลิก</TableHead>
                  <TableHead>สถานะ</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mockCampaigns.map((campaign) => {
                  const typeConfig = campaignTypeConfig[campaign.type]
                  const openRate = campaign.sent > 0 ? ((campaign.opened / campaign.sent) * 100).toFixed(1) : '0'
                  const clickRate = campaign.sent > 0 ? ((campaign.clicked / campaign.sent) * 100).toFixed(1) : '0'
                  
                  return (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          {campaign.scheduledAt && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {campaign.scheduledAt}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={cn("border-0 gap-1", typeConfig.color)}>
                          {typeConfig.icon} {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{campaign.targetAudience}</TableCell>
                      <TableCell className="text-center">{formatNumber(campaign.totalRecipients)}</TableCell>
                      <TableCell className="text-center">
                        {campaign.status === 'running' ? (
                          <div className="space-y-1">
                            <p className="text-sm">{formatNumber(campaign.sent)}</p>
                            <Progress value={(campaign.sent / campaign.totalRecipients) * 100} className="h-1" />
                          </div>
                        ) : (
                          formatNumber(campaign.sent)
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div>
                          <p>{formatNumber(campaign.opened)}</p>
                          <p className="text-xs text-muted-foreground">{openRate}%</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div>
                          <p>{formatNumber(campaign.clicked)}</p>
                          <p className="text-xs text-muted-foreground">{clickRate}%</p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem className="gap-2">
                              <Eye className="h-4 w-4" /> ดูรายละเอียด
                            </DropdownMenuItem>
                            {campaign.status === 'running' && (
                              <DropdownMenuItem className="gap-2">
                                <Pause className="h-4 w-4" /> หยุดชั่วคราว
                              </DropdownMenuItem>
                            )}
                            {campaign.status === 'paused' && (
                              <DropdownMenuItem className="gap-2">
                                <Play className="h-4 w-4" /> ดำเนินการต่อ
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="gap-2">
                              <Copy className="h-4 w-4" /> ทำสำเนา
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="gap-2 text-destructive">
                              <Trash2 className="h-4 w-4" /> ลบ
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Popups Tab */}
        <TabsContent value="popups" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {mockPopups.map((popup) => (
              <Card key={popup.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{popup.name}</CardTitle>
                      <CardDescription className="text-xs">
                        Trigger: {popup.trigger === 'immediate' ? 'แสดงทันที' : 
                                 popup.trigger === 'delay' ? 'หน่วงเวลา' :
                                 popup.trigger === 'exit' ? 'Exit Intent' : 'Scroll'}
                      </CardDescription>
                    </div>
                    {getStatusBadge(popup.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 rounded-lg bg-muted/30 text-sm">
                    "{popup.content}"
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-lg bg-muted/30">
                      <p className="font-semibold">{formatNumber(popup.views)}</p>
                      <p className="text-xs text-muted-foreground">แสดง</p>
                    </div>
                    <div className="p-2 rounded-lg bg-muted/30">
                      <p className="font-semibold">{formatNumber(popup.clicks)}</p>
                      <p className="text-xs text-muted-foreground">คลิก</p>
                    </div>
                    <div className="p-2 rounded-lg bg-muted/30">
                      <p className="font-semibold text-win">{popup.conversionRate}%</p>
                      <p className="text-xs text-muted-foreground">Conv.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 gap-1">
                      <Edit className="h-3 w-3" /> แก้ไข
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 gap-1">
                      <Eye className="h-3 w-3" /> ดูตัวอย่าง
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Broadcast Tab */}
        <TabsContent value="broadcast" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-primary" />
                ส่งข้อความถึงสมาชิกทั้งหมด
              </CardTitle>
              <CardDescription>ส่งข้อความประกาศหรือแจ้งเตือนถึงสมาชิกทุกคน</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>หัวข้อ</Label>
                  <Input placeholder="กรอกหัวข้อข้อความ" />
                </div>
                <div className="space-y-2">
                  <Label>ช่องทาง</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="gap-1">
                      <Smartphone className="h-4 w-4" /> SMS
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1">
                      <MessageSquare className="h-4 w-4" /> LINE
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1">
                      <Mail className="h-4 w-4" /> Email
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1">
                      <Bell className="h-4 w-4" /> Push
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>ข้อความ</Label>
                <textarea 
                  className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="กรอกข้อความที่ต้องการส่ง..."
                />
              </div>

              <div className="space-y-2">
                <Label>กลุ่มเป้าหมาย</Label>
                <Select defaultValue="all">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">สมาชิกทั้งหมด (25,000 คน)</SelectItem>
                    <SelectItem value="active">สมาชิก Active (18,500 คน)</SelectItem>
                    <SelectItem value="vip">สมาชิก VIP (1,500 คน)</SelectItem>
                    <SelectItem value="new">สมาชิกใหม่ 7 วัน (850 คน)</SelectItem>
                    <SelectItem value="inactive">สมาชิก Inactive (4,200 คน)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch id="schedule" />
                    <Label htmlFor="schedule">ตั้งเวลาส่ง</Label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline">บันทึกแบบร่าง</Button>
                  <Button className="gap-2">
                    <Send className="h-4 w-4" />
                    ส่งข้อความ
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Broadcasts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">ประวัติการส่ง Broadcast</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {mockCampaigns.filter(c => c.type === 'broadcast').map((campaign) => (
                  <div key={campaign.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium">{campaign.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {campaign.completedAt} • {formatNumber(campaign.totalRecipients)} ผู้รับ
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">เปิดอ่าน {formatNumber(campaign.opened)}</p>
                      <p className="text-xs text-muted-foreground">
                        {((campaign.opened / campaign.totalRecipients) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Banner Dialog */}
      <Dialog open={showCreateBannerDialog} onOpenChange={setShowCreateBannerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-primary" />
              สร้างแบนเนอร์ใหม่
            </DialogTitle>
            <DialogDescription>อัปโหลดภาพและตั้งค่าแบนเนอร์</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>ชื่อแบนเนอร์</Label>
              <Input placeholder="กรอกชื่อแบนเนอร์" />
            </div>
            <div className="space-y-2">
              <Label>อัปโหลดภาพ</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">คลิกเพื่ออัปโหลด หรือลากไฟล์มาวาง</p>
                <p className="text-xs text-muted-foreground">PNG, JPG, GIF (แนะนำ 1200x400px)</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ตำแหน่ง</Label>
                <Select>
                  <SelectTrigger><SelectValue placeholder="เลือกตำแหน่ง" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">หน้าแรก</SelectItem>
                    <SelectItem value="sidebar">Sidebar</SelectItem>
                    <SelectItem value="popup">Popup</SelectItem>
                    <SelectItem value="footer">Footer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ลิงก์ปลายทาง</Label>
                <Input placeholder="https://..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>วันที่เริ่ม</Label>
                <ThaiDatePicker
                  selected={null}
                  onChange={() => {}}
                  placeholder="เลือกวันที่เริ่มต้น"
                />
              </div>
              <div className="space-y-2">
                <Label>วันที่สิ้นสุด</Label>
                <ThaiDatePicker
                  selected={null}
                  onChange={() => {}}
                  placeholder="เลือกวันที่สิ้นสุด"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateBannerDialog(false)}>ยกเลิก</Button>
            <Button className="gap-2"><Save className="h-4 w-4" /> บันทึก</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateCampaignDialog} onOpenChange={setShowCreateCampaignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              สร้างแคมเปญใหม่
            </DialogTitle>
            <DialogDescription>ตั้งค่าแคมเปญการตลาด</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>ชื่อแคมเปญ</Label>
              <Input placeholder="กรอกชื่อแคมเปญ" />
            </div>
            <div className="space-y-2">
              <Label>ประเภท</Label>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(campaignTypeConfig).map(([key, config]) => (
                  <Button key={key} variant="outline" size="sm" className="gap-1 flex-col h-auto py-2">
                    {config.icon}
                    <span className="text-xs">{config.label}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>กลุ่มเป้าหมาย</Label>
              <Select>
                <SelectTrigger><SelectValue placeholder="เลือกกลุ่มเป้าหมาย" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">สมาชิกทั้งหมด</SelectItem>
                  <SelectItem value="active">สมาชิก Active</SelectItem>
                  <SelectItem value="vip">สมาชิก VIP</SelectItem>
                  <SelectItem value="new">สมาชิกใหม่</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>ข้อความ</Label>
              <textarea 
                className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="กรอกข้อความ..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCampaignDialog(false)}>ยกเลิก</Button>
            <Button className="gap-2"><Save className="h-4 w-4" /> สร้างแคมเปญ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Popup Dialog */}
      <Dialog open={showCreatePopupDialog} onOpenChange={setShowCreatePopupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layout className="h-5 w-5 text-primary" />
              สร้างป๊อปอัพใหม่
            </DialogTitle>
            <DialogDescription>ตั้งค่าป๊อปอัพโปรโมชั่น</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>ชื่อป๊อปอัพ</Label>
              <Input placeholder="กรอกชื่อป๊อปอัพ" />
            </div>
            <div className="space-y-2">
              <Label>Trigger</Label>
              <Select>
                <SelectTrigger><SelectValue placeholder="เลือก Trigger" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">แสดงทันที</SelectItem>
                  <SelectItem value="delay">หน่วงเวลา (5 วินาที)</SelectItem>
                  <SelectItem value="exit">Exit Intent</SelectItem>
                  <SelectItem value="scroll">เลื่อนหน้า 50%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>อัปโหลดภาพ</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">คลิกเพื่ออัปโหลด</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>ข้อความ</Label>
              <Input placeholder="ข้อความที่จะแสดง" />
            </div>
            <div className="space-y-2">
              <Label>ลิงก์ปุ่ม</Label>
              <Input placeholder="https://..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePopupDialog(false)}>ยกเลิก</Button>
            <Button className="gap-2"><Save className="h-4 w-4" /> บันทึก</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}










